import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, InputAdornment, MenuItem, TextField } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import marketplaceService, {
    extractErrorMessage,
    GroupPricing,
    ListingVisibility,
    SellerListing,
    SellerListingPayload,
} from '../../../services/marketplaceService';
import { formatEuro } from '../../../pages/marketplaceVisibility';
import MaterialSymbol from '../../common/MaterialSymbol';
import VisibilityChoice from './VisibilityChoice';
import CoursePicker from './CoursePicker';
import TagInput from './TagInput';
import '../marketplace-design.css';

export interface ListingEditorDialogProps {
    open: boolean;
    /** null = neues Angebot */
    listing: SellerListing | null;
    hasGroup: boolean;
    groupMembers: Array<{ id: number; name: string | null }>;
    groupTotal: number;
    /** Kurse, die schon in einem anderen Angebot des Mandanten stecken (im Wizard nicht wählbar) */
    takenCourseIds?: number[];
    /** Plattformgebühr in Prozent für neue Angebote (Anzeige) */
    defaultFeePercent?: string;
    onClose: () => void;
    /** Nach dem Speichern; `submitted` = zusätzlich eingereicht */
    onSaved: (listing: SellerListing, submitted: boolean) => void;
}

interface FormState {
    /** Geordnet; der erste Kurs ist der Primärkurs */
    courseIds: number[];
    title: string;
    description: string;
    tags: string[];
    categoryId: number | '';
    allowReference: boolean;
    allowCopy: boolean;
    allowSeatCopy: boolean;
    priceReference: string;
    priceCopy: string;
    priceSeat: string;
    visibility: ListingVisibility;
    groupPricing: GroupPricing;
    priceGroup: string;
}

const emptyForm = (): FormState => ({
    courseIds: [],
    title: '',
    description: '',
    tags: [],
    categoryId: '',
    allowReference: true,
    allowCopy: true,
    allowSeatCopy: false,
    priceReference: '0.00',
    priceCopy: '0.00',
    priceSeat: '',
    visibility: 'public',
    groupPricing: 'free',
    priceGroup: '',
});

const fromListing = (l: SellerListing): FormState => ({
    courseIds: l.courses?.length ? l.courses.map((c) => c.id) : [l.course.id],
    title: l.customTitle ?? '',
    description: l.description ?? '',
    tags: l.tags ?? [],
    categoryId: l.category?.id ?? '',
    allowReference: l.allowReference,
    allowCopy: l.allowCopy,
    allowSeatCopy: l.allowSeatCopy,
    priceReference: l.priceReference ?? '0.00',
    priceCopy: l.priceCopy ?? '0.00',
    priceSeat: l.priceSeat ?? '',
    visibility: l.visibility,
    groupPricing: l.groupPricing,
    priceGroup: l.priceGroup ?? '',
});

const num = (v: string): number => (v.trim() === '' ? NaN : parseFloat(v));

const STEPS = ['course', 'pricing', 'visibility'] as const;

/**
 * Drei Schritte (Plan 3.7): Kurs & Texte → Lizenzmodelle & Preise → Wo
 * anbieten. Im Zustand `published` (editMode `content`) sind Preise,
 * Lizenzmodelle und Sichtbarkeit gesperrt — Preisänderung = zurückziehen und
 * neu einreichen (Plan 3.2).
 */
const ListingEditorDialog: React.FC<ListingEditorDialogProps> = ({
    open,
    listing,
    hasGroup,
    groupMembers,
    groupTotal,
    takenCourseIds = [],
    defaultFeePercent = '30.00',
    onClose,
    onSaved,
}) => {
    const translate = useTranslate();
    const notify = useNotify();

    const [step, setStep] = useState(0);
    const [form, setForm] = useState<FormState>(emptyForm());
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [courses, setCourses] = useState<{ id: number; title: string }[]>([]);
    const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
    const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);

    // Titel der schon gewählten Kurse kennt das Angebot selbst, falls die Kursliste sie nicht (mehr) enthält
    const pickerCourses = useMemo(() => {
        const known = new Map(courses.map((c) => [c.id, c]));
        (listing?.courses ?? []).forEach((c) => {
            if (!known.has(c.id)) known.set(c.id, { id: c.id, title: c.title });
        });
        return Array.from(known.values());
    }, [courses, listing]);

    const isEdit = listing !== null;
    const editMode = listing?.editMode ?? 'all';
    const locked = editMode === 'content';
    const feePercent = listing?.platformFeePercent ?? defaultFeePercent;

    useEffect(() => {
        if (!open) return;
        setStep(0);
        setError(null);
        setForm(listing ? fromListing(listing) : emptyForm());
    }, [open, listing]);

    useEffect(() => {
        if (!open) return;
        marketplaceService
            .getOwnCourses()
            .then(setCourses)
            .catch(() => setCourses([]));
        marketplaceService
            .getSellerTags()
            .then(setTagSuggestions)
            .catch(() => setTagSuggestions([]));
    }, [open]);

    useEffect(() => {
        if (!open) return;
        marketplaceService
            .getCategories()
            .then(setCategories)
            .catch(() => setCategories([]));
    }, [open]);

    const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

    /** Fehlermeldung (Übersetzungsschlüssel) des Schritts oder null. */
    const stepError = (index: number): string | null => {
        if (index === 0 && !locked && form.courseIds.length === 0) return 'marketplaceSeller.editor.errors.course';
        if (index === 0 && !locked && form.courseIds.length > 1 && form.title.trim() === '') return 'marketplaceSeller.editor.errors.bundleTitle';
        if (index === 1 && !locked) {
            if (!form.allowReference && !form.allowCopy && !form.allowSeatCopy) return 'marketplaceSeller.editor.errors.noModel';
            const prices: Array<[boolean, string]> = [
                [form.allowReference, form.priceReference],
                [form.allowCopy, form.priceCopy],
                [form.allowSeatCopy, form.priceSeat],
            ];
            if (prices.some(([on, v]) => on && !(num(v) >= 0))) return 'marketplaceSeller.editor.errors.price';
        }
        if (index === 2 && !locked && form.groupPricing === 'internal' && !(num(form.priceGroup) > 0)) {
            return 'marketplaceSeller.editor.errors.priceGroup';
        }
        return null;
    };

    const payload = useMemo((): SellerListingPayload => {
        const content: SellerListingPayload = {
            description: form.description.trim() || null,
            tags: form.tags,
            categoryId: form.categoryId === '' ? null : form.categoryId,
        };
        if (locked) return content;
        return {
            ...content,
            courseIds: form.courseIds,
            title: form.title.trim() || null,
            allowReference: form.allowReference,
            allowCopy: form.allowCopy,
            allowSeatCopy: form.allowSeatCopy,
            priceReference: form.allowReference ? form.priceReference : '0.00',
            priceCopy: form.allowCopy ? form.priceCopy : '0.00',
            priceSeat: form.allowSeatCopy && form.priceSeat !== '' ? form.priceSeat : null,
            visibility: form.visibility,
            groupPricing: form.groupPricing,
            priceGroup: form.groupPricing === 'internal' ? form.priceGroup : null,
        };
    }, [form, locked]);

    const save = async (submit: boolean) => {
        for (let i = 0; i < STEPS.length; i += 1) {
            const err = stepError(i);
            if (err) {
                setStep(i);
                setError(translate(err));
                return;
            }
        }
        setSaving(true);
        setError(null);
        try {
            let saved = listing
                ? await marketplaceService.updateSellerListing(listing.id, payload)
                : await marketplaceService.createSellerListing(payload);
            if (submit) {
                saved = await marketplaceService.submitSellerListing(saved.id);
            }
            notify(translate(submit ? 'marketplaceSeller.editor.submitted' : 'marketplaceSeller.editor.saved'), { type: 'success' });
            onSaved(saved, submit);
        } catch (err) {
            setError(extractErrorMessage(err, translate('marketplaceSeller.editor.errors.save')));
        } finally {
            setSaving(false);
        }
    };

    const next = () => {
        const err = stepError(step);
        if (err) {
            setError(translate(err));
            return;
        }
        setError(null);
        setStep((s) => Math.min(STEPS.length - 1, s + 1));
    };

    const netShare = (price: string): string | null => {
        const p = num(price);
        if (form.visibility !== 'public' || !(p > 0)) return null;
        return formatEuro(p * (1 - parseFloat(feePercent) / 100));
    };

    const priceField = (
        key: 'priceReference' | 'priceCopy' | 'priceSeat',
        enabledKey: 'allowReference' | 'allowCopy' | 'allowSeatCopy',
        labelKey: string,
        helpKey: string,
    ) => (
        <div className={`mk-optc${form[enabledKey] ? ' on' : ''}`}>
            <input
                id={`mk-model-${enabledKey}`}
                type="checkbox"
                checked={form[enabledKey]}
                disabled={locked}
                aria-label={translate(labelKey)}
                onChange={(e) => set(enabledKey, e.target.checked)}
            />
            <div className="ot">
                <label htmlFor={`mk-model-${enabledKey}`} style={{ display: 'block', cursor: locked ? 'default' : 'pointer' }}>
                    <b>{translate(labelKey)}</b>
                    <p>{translate(helpKey)}</p>
                </label>
                {form[enabledKey] && (
                    <div className="pr">
                        <TextField
                            label={translate(key === 'priceSeat' ? 'marketplaceSeller.editor.priceSeat' : 'marketplaceSeller.editor.priceNet')}
                            type="number"
                            value={form[key]}
                            disabled={locked}
                            onChange={(e) => set(key, e.target.value)}
                            size="small"
                            sx={{ maxWidth: 260 }}
                            slotProps={{
                                htmlInput: { min: 0, step: '0.01' },
                                input: { endAdornment: <InputAdornment position="end">€</InputAdornment> },
                            }}
                            helperText={
                                netShare(form[key])
                                    ? translate('marketplaceSeller.editor.yourShare', { amount: netShare(form[key]) })
                                    : undefined
                            }
                        />
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            scroll="paper"
            className="mk-wiz mk-wiz-scrim"
            aria-labelledby="mk-wiz-title"
            slotProps={{ paper: { sx: { width: 680, maxWidth: '100%' } } }}
        >
            <div className="mk-wh">
                <h2 id="mk-wiz-title">
                    {translate(isEdit ? 'marketplaceSeller.editor.titleEdit' : 'marketplaceSeller.editor.titleNew')}
                </h2>
                <button type="button" className="mk-wclose" onClick={onClose} disabled={saving} aria-label={translate('marketplaceBuyer.checkout.close')}>
                    <MaterialSymbol name="close" size={24} />
                </button>
            </div>
            <div className="mk-steps" role="list" aria-label={translate('marketplaceSeller.editor.stepOf', { current: step + 1, total: STEPS.length })}>
                {STEPS.map((s, index) => (
                    <div key={s} role="listitem" className={`mk-step${index === step ? ' cur' : index < step ? ' done' : ''}`} aria-current={index === step ? 'step' : undefined}>
                        <span className="dot">{index < step ? <MaterialSymbol name="check" size={16} /> : index + 1}</span>
                        {translate(`marketplaceSeller.editor.steps.${s}`)}
                    </div>
                ))}
            </div>

            <div className="mk-wb">
                {error && (
                    <div className="mk-alert err" role="alert" style={{ marginTop: 8 }}>
                        <MaterialSymbol name="error" />
                        <div className="body">{error}</div>
                    </div>
                )}
                {locked && (
                    <div className="mk-alert info" role="note" style={{ marginTop: 8 }}>
                        <MaterialSymbol name="info" />
                        <div className="body"><p style={{ color: 'var(--oa-text)' }}>{translate('marketplaceSeller.editor.lockedHint')}</p></div>
                    </div>
                )}
                {listing?.status === 'rejected' && listing.rejectionReason && (
                    <div className="mk-alert warn" role="note" style={{ marginTop: 8 }}>
                        <MaterialSymbol name="warning" />
                        <div className="body"><p style={{ color: 'var(--oa-text)' }}>{translate('marketplaceSeller.editor.rejectedHint', { reason: listing.rejectionReason })}</p></div>
                    </div>
                )}

                {step === 0 && (
                    <div className="mk-fields" style={{ marginTop: 10 }}>
                        <div>
                            <label className="mk-flabel" id="mk-courses-label">{translate('marketplaceSeller.editor.courses')}</label>
                            <CoursePicker
                                courses={pickerCourses}
                                selectedIds={form.courseIds}
                                onChange={(ids) => set('courseIds', ids)}
                                takenIds={takenCourseIds}
                                disabled={locked}
                            />
                            <p className="mk-hint">{translate('marketplaceSeller.editor.coursesHelp')}</p>
                        </div>
                        <TextField
                            label={translate('marketplaceSeller.editor.title')}
                            helperText={translate(form.courseIds.length > 1 ? 'marketplaceSeller.editor.titleHelpBundle' : 'marketplaceSeller.editor.titleHelp')}
                            required={form.courseIds.length > 1}
                            value={form.title}
                            disabled={locked}
                            onChange={(e) => set('title', e.target.value)}
                            size="small"
                        />
                        <TextField
                            label={translate('marketplaceSeller.editor.description')}
                            value={form.description}
                            onChange={(e) => set('description', e.target.value)}
                            multiline
                            minRows={4}
                        />
                        <TagInput
                            label={translate('marketplaceSeller.editor.tags')}
                            helperText={translate('marketplaceSeller.editor.tagsHelp')}
                            value={form.tags}
                            onChange={(tags) => set('tags', tags)}
                            suggestions={tagSuggestions}
                            removeLabel={(tag) => translate('marketplaceSeller.editor.removeTag', { tag })}
                        />
                        {categories.length > 0 && (
                            <TextField
                                select
                                label={translate('marketplaceSeller.editor.category')}
                                value={form.categoryId}
                                onChange={(e) => set('categoryId', e.target.value === '' ? '' : Number(e.target.value))}
                                size="small"
                            >
                                <MenuItem value="">{translate('marketplaceSeller.editor.noCategory')}</MenuItem>
                                {categories.map((c) => (
                                    <MenuItem key={c.id} value={c.id}>
                                        {c.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                    </div>
                )}

                {step === 1 && (
                    <div style={{ marginTop: 10 }}>
                        {priceField('priceReference', 'allowReference', 'marketplaceSeller.editor.models.reference', 'marketplaceSeller.editor.models.referenceHelp')}
                        {priceField('priceCopy', 'allowCopy', 'marketplaceSeller.editor.models.copy', 'marketplaceSeller.editor.models.copyHelp')}
                        {priceField('priceSeat', 'allowSeatCopy', 'marketplaceSeller.editor.models.seat', 'marketplaceSeller.editor.models.seatHelp')}
                        <p className="mk-hint">
                            {translate('marketplaceSeller.editor.pricesNetHint')}{' '}
                            {translate('marketplaceSeller.editor.feeHint', { fee: feePercent.replace(/\.00$/, '') })}
                        </p>
                    </div>
                )}

                {step === 2 && (
                    <div style={{ marginTop: 10 }}>
                        <VisibilityChoice
                            visibility={form.visibility}
                            onVisibilityChange={(v) => {
                                set('visibility', v);
                                // „gleicher Preis" gibt es nur für öffentliche Angebote
                                if (v === 'group' && form.groupPricing === 'same') set('groupPricing', 'free');
                            }}
                            groupPricing={form.groupPricing}
                            onGroupPricingChange={(v) => set('groupPricing', v)}
                            priceGroup={form.priceGroup}
                            onPriceGroupChange={(v) => set('priceGroup', v)}
                            hasGroup={hasGroup}
                            groupMembers={groupMembers}
                            groupTotal={groupTotal}
                            platformFeePercent={feePercent.replace(/\.00$/, '')}
                            disabled={locked}
                        />
                    </div>
                )}
            </div>

            <div className="mk-wf">
                <button type="button" className="mk-btn-text" onClick={onClose} disabled={saving}>
                    {translate('ra.action.cancel')}
                </button>
                <div className="r">
                    {step > 0 && (
                        <button type="button" className="mk-btn-o mk-sm" onClick={() => setStep((s) => s - 1)} disabled={saving}>
                            {translate('ra.action.back')}
                        </button>
                    )}
                    {step < STEPS.length - 1 ? (
                        <button type="button" className="mk-btn-s mk-sm" onClick={next} disabled={saving}>
                            {translate('marketplaceSeller.editor.next')}
                        </button>
                    ) : (
                        <>
                            <button type="button" className="mk-btn-o mk-sm" onClick={() => save(false)} disabled={saving}>
                                {translate(isEdit ? 'marketplaceSeller.editor.save' : 'marketplaceSeller.editor.saveDraft')}
                            </button>
                            {!locked && (
                                <button type="button" className="mk-btn-s mk-sm" onClick={() => save(true)} disabled={saving}>
                                    {translate('marketplaceSeller.editor.saveAndSubmit')}
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </Dialog>
    );
};

export default ListingEditorDialog;
